import React, { useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../context/AppContext';

/**
 * Interactive 3D ViewCube overlay — professional-grade view orientation control,
 * modelled after the navigation cubes in Blender / 3ds Max / Unity.
 *
 * Features:
 *  - 6 face click targets (FRONT / BACK / LEFT / RIGHT / TOP / BOTTOM) → axis-aligned ortho views
 *  - 8 corner click targets → isometric / angled perspective views
 *  - Bidirectional sync: rotates live with the main camera as the user orbits
 *  - Click triggers a smooth animated camera transition (handled by BabylonScene)
 *  - Does NOT replace pan/zoom/orbit on the main canvas; purely additive
 *
 * Rendering: pure CSS 3D (no extra Babylon engine / canvas) so it's virtually
 * free in GPU/CPU and can't leak WebGL contexts.
 */
const SIZE = 56;       // visual cube size in px (half of original 112)
const HALF = SIZE / 2;
const FACE_BG = 'rgba(39,39,42,0.92)';           // zinc-800/92
const FACE_BG_HOVER = 'rgba(139,92,246,0.85)';   // violet-500/85
const FACE_BORDER = 'rgba(161,161,170,0.45)';    // zinc-400/45
const CORNER_DOT = 'rgba(236,72,153,0.85)';      // fuchsia-500/85
const CORNER_SIZE = 10;                           // corner hotspot dot size (scaled with cube)

// 6 axis-aligned faces and the camera preset they map to.
const FACES = [
  { label: 'FRONT',  preset: 'front',  transform: `translateZ(${HALF}px)` },
  { label: 'BACK',   preset: 'back',   transform: `rotateY(180deg) translateZ(${HALF}px)` },
  { label: 'RIGHT',  preset: 'right',  transform: `rotateY(90deg) translateZ(${HALF}px)` },
  { label: 'LEFT',   preset: 'left',   transform: `rotateY(-90deg) translateZ(${HALF}px)` },
  { label: 'TOP',    preset: 'top',    transform: `rotateX(90deg) translateZ(${HALF}px)` },
  { label: 'BOT',    preset: 'bottom', transform: `rotateX(-90deg) translateZ(${HALF}px)` },
];

// 8 corners → isometric views. alpha/beta were chosen to match Babylon's ArcRotateCamera
// convention so each corner click lines the cube up with the camera at that corner.
const CORNER_PRESETS = [
  // (x, y, z) sign combinations (±1,±1,±1)
  { xs:  1, ys:  1, zs:  1, alpha: -Math.PI * 3 / 4, beta: Math.PI / 3 },   // Front-Top-Right
  { xs: -1, ys:  1, zs:  1, alpha: -Math.PI / 4,     beta: Math.PI / 3 },   // Front-Top-Left
  { xs:  1, ys: -1, zs:  1, alpha: -Math.PI * 3 / 4, beta: 2 * Math.PI / 3 }, // Front-Bottom-Right
  { xs: -1, ys: -1, zs:  1, alpha: -Math.PI / 4,     beta: 2 * Math.PI / 3 }, // Front-Bottom-Left
  { xs:  1, ys:  1, zs: -1, alpha:  Math.PI * 3 / 4, beta: Math.PI / 3 },   // Back-Top-Right
  { xs: -1, ys:  1, zs: -1, alpha:  Math.PI / 4,     beta: Math.PI / 3 },   // Back-Top-Left
  { xs:  1, ys: -1, zs: -1, alpha:  Math.PI * 3 / 4, beta: 2 * Math.PI / 3 }, // Back-Bottom-Right
  { xs: -1, ys: -1, zs: -1, alpha:  Math.PI / 4,     beta: 2 * Math.PI / 3 }, // Back-Bottom-Left
];

const ViewCube = () => {
  const { mainCameraRef, setViewPreset } = useApp();
  const cubeRef = useRef(null);
  const rafRef = useRef(null);

  // Each frame: read live main-camera alpha/beta, convert to a CSS rotateX/Y.
  // With Babylon ArcRotateCamera at (alpha=-π/2, beta=π/2) the viewer sees the world
  // from +Z direction → cube's FRONT face (translateZ(+HALF)) should face the user.
  // rotateY(−(alpha + π/2)) + rotateX(β − π/2) achieves that mapping.
  const syncFrame = useCallback(() => {
    const camera = mainCameraRef?.current;
    const node = cubeRef.current;
    if (camera && node) {
      const alpha = camera.alpha || 0;
      const beta = camera.beta || Math.PI / 2;
      const yDeg = -((alpha + Math.PI / 2) * 180) / Math.PI;
      const xDeg = ((beta - Math.PI / 2) * 180) / Math.PI;
      node.style.transform = `rotateX(${xDeg}deg) rotateY(${yDeg}deg)`;
    }
    rafRef.current = requestAnimationFrame(syncFrame);
  }, [mainCameraRef]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(syncFrame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [syncFrame]);

  const handleFaceClick = useCallback((preset, e) => {
    e.stopPropagation();
    setViewPreset(preset);
  }, [setViewPreset]);

  const handleCornerClick = useCallback((p, e) => {
    e.stopPropagation();
    setViewPreset({ type: 'iso', alpha: p.alpha, beta: p.beta });
  }, [setViewPreset]);

  const handleHome = useCallback((e) => {
    e.stopPropagation();
    setViewPreset('reset');
  }, [setViewPreset]);

  return (
    <div
      className="absolute top-14 right-3 z-20 select-none"
      style={{ width: SIZE + 24, height: SIZE + 28 }}
      data-testid="view-cube-container"
    >
      {/* Small perspective container for the 3D cube */}
      <div style={{ width: SIZE + 24, height: SIZE + 24, perspective: 260, position: 'relative' }}>
        {/* Rotating cube */}
        <div
          ref={cubeRef}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: SIZE,
            height: SIZE,
            transformStyle: 'preserve-3d',
            transition: 'transform 0.05s linear',
            willChange: 'transform',
          }}
          data-testid="view-cube-rotating"
        >
          {FACES.map(f => (
            <button
              key={f.preset}
              onClick={(e) => handleFaceClick(f.preset, e)}
              title={`${f.label} view`}
              data-testid={`view-cube-face-${f.preset}`}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: SIZE, height: SIZE,
                transform: f.transform,
                background: FACE_BG,
                border: `1px solid ${FACE_BORDER}`,
                color: '#e4e4e7',
                fontFamily: 'system-ui, sans-serif',
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: 0.5,
                cursor: 'pointer',
                backdropFilter: 'blur(6px)',
                padding: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = FACE_BG_HOVER; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = FACE_BG; }}
            >
              {f.label}
            </button>
          ))}

          {/* 8 corner hotspots — small pink dots at each ±HALF cube corner. */}
          {CORNER_PRESETS.map((p, idx) => (
            <button
              key={`c${idx}`}
              onClick={(e) => handleCornerClick(p, e)}
              title="Isometric view"
              data-testid={`view-cube-corner-${idx}`}
              style={{
                position: 'absolute',
                top: HALF - CORNER_SIZE / 2, left: HALF - CORNER_SIZE / 2,
                width: CORNER_SIZE, height: CORNER_SIZE,
                borderRadius: '50%',
                transform: `translate3d(${p.xs * HALF}px, ${-p.ys * HALF}px, ${p.zs * HALF}px)`,
                background: CORNER_DOT,
                border: '1px solid rgba(255,255,255,0.85)',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
        </div>
      </div>

      {/* Home button below the cube — returns to default perspective view */}
      <button
        onClick={handleHome}
        title="Reset to default perspective"
        data-testid="view-cube-home-btn"
        className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] px-1.5 py-0.5 rounded bg-zinc-800/80 hover:bg-fuchsia-600/80 text-zinc-200 border border-zinc-600"
      >
        ⌂ HOME
      </button>
    </div>
  );
};

export default ViewCube;
